<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    {{ $t('org.dashboard.Cost by region') }}
                </h4>
            </div>
        </div>
        <b-table
            class="table-region dashboard-chart"
            tableClass="h-100"
            :class="{ 'dashboard-edit': editMode, 'table-no-data': !tableData.length  }"
            :items="tableData"
            :fields="fields"
            borderless
            sticky-header
            show-empty
        >
            <template v-slot:empty>
                <div class="pb-50 pt-2 text-center">
                    <SVGEmptyIcon/>
                    <div>
                        {{ $t('org.dashboard.No data') }}
                    </div>
                </div>
            </template>
            <template v-slot:cell(key)="items">
                <div
                    class="d-flex align-items-center"
                >
                    <b-avatar
                        rounded="sm"
                        size="34"
                        :variant="items.item.regionType === 'AWS Region' ? 'light-primary' : 'light-warning'"
                    >
                        <SVGRegionIcon v-if="items.item.regionType === 'AWS Region'"/>
                        <SVGEdgeIcon v-else/>
                    </b-avatar>
                    <div
                        class="d-flex flex-column ml-1"
                    >
                        <span
                            class="line-height-1 font-small-4"
                        >
                            {{ items.item.key }}
                        </span>
                        <small
                            class="line-height-1 font-small-3 mt-25"
                            style="color: #929292"
                        >
                            {{ items.item.regionName }}
                        </small>
                    </div>
                </div>
            </template>
            <template v-slot:cell(lastMonthCost)="items">
                {{ typeof items.value === 'number' ? $_getDollar(items.value) : '-' }}
            </template>
            <template v-slot:cell(thisMonthCost)="items">
                <span
                    :class="{'data-with-event': (typeof items.value === 'number')}"
                    @click="showDetailModal(items.item.key, items.item.thisMonthCost)"
                >
                    {{ typeof items.value === 'number' ? $_getDollar(items.value) : '-' }}
                </span>
            </template>
            <template v-slot:cell(diffCost)="items">
                <div
                    class="d-flex flex-column text-right"
                >
                    <span>
                        {{ $_getDollar(items.value) }}
                    </span>
                    <span
                        v-if="!!items.item.lastMonthCost"
                        class="font-small-3"
                        :class="items.item.rate > 0 ? 'text-danger' : 'text-success'"
                    >
                        <feather-icon
                            :icon="items.item.rate > 0 ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            size="16"
                        />
                        {{ items.item.rate >= 0 ? items.item.rate : Math.abs(items.item.rate) }}%
                    </span>
                    <span v-else>-</span>
                </div>
            </template>
        </b-table>
    </b-overlay>
</template>

<script>
import { getCurrentInstance, watch, computed } from '@vue/composition-api'
import useDashboard from '@/composables/useDashboard'
import useCommonData from '@/composables/useCommonData'
import SVGRegionIcon from '@/assets/images/icons/regions/icon_region.svg'
import SVGEdgeIcon from '@/assets/images/icons/regions/icon_edge.svg'
import SVGEmptyIcon from '@/assets/images/empty.svg'
export default {
    components: {
        SVGRegionIcon,
        SVGEdgeIcon,
        SVGEmptyIcon
    },
    props: ['editMode'],
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { __getRegionNameByKey, __getRegionTypeByKey } = useCommonData()
        const {
            getSummaryRegions
        } = useDashboard()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, regionsCostData] = getSummaryRegions(currentParams.value)
        const lastMonthRegions = computed(() => regionsCostData.value.lastMonthRegions)
        const thisMonthRegions = computed(() => regionsCostData.value.thisMonthRegions)
        const thisMonthCostObj = computed(() => {
            return thisMonthRegions.value.reduce((acc, cur) => {
                if (!acc.hasOwnProperty(cur.key)) {
                    acc[cur.key] = cur.cost
                }
                return acc
            }, {})
        })
        const lastMonthCostObj = computed(() => {
            return lastMonthRegions.value.reduce((acc, cur) => {
                if (!acc.hasOwnProperty(cur.key)) {
                    acc[cur.key] = cur.cost
                }
                return acc
            }, {})
        })
        const getObjectsKeysArr = () => {
            const concatArr = Object.keys(thisMonthCostObj.value).concat(Object.keys(lastMonthCostObj.value))
            const removedDuplicatedArr = concatArr.filter((item, pos) => {
                return concatArr.indexOf(item) === pos
            })
            return removedDuplicatedArr
        }
        const tableData = computed(() => {
            return getObjectsKeysArr().map(key => {
                const diffCost = (thisMonthCostObj.value[key] ?? 0) - (lastMonthCostObj.value[key] ?? 0)
                return {
                    key: key,
                    regionName: __getRegionNameByKey(key),
                    regionType: __getRegionTypeByKey(key),
                    lastMonthCost: lastMonthCostObj.value[key],
                    thisMonthCost: thisMonthCostObj.value[key],
                    diffCost,
                    rate: (Math.round(diffCost / lastMonthCostObj.value[key] * 1000) / 10)
                }
            })
        })

        const fields = computed(() => [
            {
                label: vm.$t('v4.Region'),
                key: 'key',
                sortable: false,
                tdClass: ['text-nowrap', 'width-30-per', 'font-small-4', 'py-1']
            },
            {
                label: vm.$t('v4.Prev'),
                key: 'lastMonthCost',
                sortable: false,
                tdClass: ['text-nowrap', 'width-25-per', 'font-small-4']
            },
            {
                label: vm.$t('v4.This Month'),
                key: 'thisMonthCost',
                sortable: false,
                tdClass: ['text-nowrap', 'width-25-per', 'font-small-4']
            },
            {
                label: '',
                key: 'diffCost',
                sortable: false,
                tdClass: ['text-nowrap', 'width-20-per', 'font-small-4']
            }
        ])
        const showDetailModal = (region, thisMonthCost) => {
            if (typeof thisMonthCost !== 'number') return
            if (region.toLowerCase() !== 'etc') {
                context.emit('modal:showDetail', { region })
            }
        }
        watch(currentParams, (value, oldValue) => {
            getSummaryRegions(value)
        }, {
            deep: true
        })
        return {
            fields,
            tableData,
            status,
            showDetailModal
        }
    }
}
</script>

<style>
.table-region.b-table-sticky-header {
    margin-bottom: 8px;
    max-height: 85% !important;
}
.table-region.b-table-sticky-header.table-no-data {
    height: calc(100% - 62px);
}
.table-region.dashboard-edit {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.table-region.dashboard-edit::-webkit-scrollbar {
    display: none;
}
.table-region table {
    border-collapse: separate;
	border-spacing: 0;
}
.table-region tr td {
    padding: 1rem 2rem;
}
.table-region tr:first-child td {
    padding-top: 23px !important;
    padding-bottom: 1.2rem !important;
}

.table-region thead tr th {
    font-size: 0.9rem !important;
    padding: 13px 24px !important;
    text-transform: capitalize !important;
    font-weight: inherit !important;
    background-color: #fff !important;
    border-top: 1px solid #DBDBDB !important;
    border-bottom: 1px solid #DBDBDB !important;
}
.dark-layout .table-region thead tr th {
    background-color: #283046 !important;
}
</style>
